import { DefaultTheme, NavigationContainer } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { Modals } from 'components/Modals';
import { MODALS } from 'components/ModalsConfig';
import { navigationRef } from 'components/Navigation/navigationRef';
import {
  ModalScreens,
  NavigationStacks,
  Screens,
  Stacks,
} from 'components/Navigation/types';
import { useSelector } from 'hooks/useSelector';
import * as React from 'react';
import { Platform } from 'react-native';
import { useDispatch } from 'react-redux';
import { RepeatableStackScreen } from 'screens/AssessmentScreen/RepeatableStackScreen';
import { AssessmentScreen } from 'screens/AssessmentScreen/AssessmentScreen';
import { AuthScreen } from 'screens/AuthScreen/AuthScreen';
import { HomeWithoutSidebarIos26 } from 'screens/TabScreen/HomeWithoutSidebarIos26';
import {
  selectNavigationState,
  selectHasSidebarNavigation,
  selectAppType,
} from 'store/app/selectors';
import { TabError } from 'screens/TabError';
import { IsInModalProvider } from 'hooks/isInModal';
import { AppBackgroundImage } from 'componentsAware/AppBackgroundImage';
import { HomeWithoutSidebar } from 'screens/TabScreen/HomeWithoutSidebar';
import { HomeWithSidebar } from 'screens/TabScreen/HomeWithSidebar';
import { selectUserId } from 'store/auth/selectors';
import { SET_NAVIGATION_STATE } from 'store/actions';

const Stack = createNativeStackNavigator<NavigationStacks[Stacks.ROOT]>();

export const RootNavigation: React.FC<{ onReady: () => void }> = ({
  onReady,
}) => {
  const dispatch = useDispatch();
  const navigationState = useSelector(selectNavigationState, () => true);
  const hasSidebarNavigation = useSelector(selectHasSidebarNavigation);
  const appType = useSelector(selectAppType);
  const userId = useSelector(selectUserId);

  return (
    <>
      <AppBackgroundImage />
      <NavigationContainer
        ref={navigationRef}
        onReady={onReady}
        documentTitle={{
          enabled: false,
        }}
        theme={{
          ...DefaultTheme,
          colors: {
            ...DefaultTheme.colors,
            background:
              userId && hasSidebarNavigation
                ? 'transparent'
                : DefaultTheme.colors.background,
          },
        }}
        initialState={navigationState || undefined}
        onStateChange={
          __DEV__ ? (state) => dispatch(SET_NAVIGATION_STATE(state)) : undefined
        }
      >
        <Stack.Navigator
          initialRouteName={userId ? Screens.RootPage : Screens.RootAuth}
          screenOptions={{
            headerShown: false,
            contentStyle: {
              backgroundColor: 'transparent',
            },
          }}
        >
          <Stack.Screen
            name={Screens.RootAuth}
            component={AuthScreen}
            options={{ animation: 'fade' }}
          />
          <Stack.Group
            navigationKey={hasSidebarNavigation ? 'no-sidebar' : 'sidebar'}
          >
            {hasSidebarNavigation ? (
              <Stack.Screen
                name={Screens.RootPage}
                component={HomeWithSidebar}
                options={{ animation: 'fade' }}
              />
            ) : (
              <Stack.Screen
                name={Screens.RootPage}
                component={
                  Platform.OS === 'ios' &&
                  Platform.Version >= '26.0.0' &&
                  appType === 'mobile'
                    ? HomeWithoutSidebarIos26
                    : HomeWithoutSidebar
                }
                options={{ animation: 'fade' }}
              />
            )}
          </Stack.Group>
          <Stack.Screen
            name={Screens.RootError}
            component={TabError}
            options={{ animation: 'fade' }}
          />
          <Stack.Screen
            name={Screens.ROOT_ASSESSMENT}
            component={AssessmentScreen}
          />
          <Stack.Screen
            name={Screens.ROOT_ASEREPEATABLE}
            component={RepeatableStackScreen}
          />
          {Object.entries(Platform.OS === 'ios' ? MODALS : {}).map(
            ([modalKey, modalProps]) => (
              <Stack.Screen
                name={ModalScreens[modalKey as ModalScreens]}
                key={modalKey}
                options={{
                  gestureEnabled: !!modalProps.gestureEnabled,
                  presentation:
                    modalProps.size === 'fullscreen'
                      ? 'transparentModal'
                      : modalProps.size === 'small'
                        ? 'formSheet'
                        : 'modal',
                }}
              >
                {(props) => (
                  <IsInModalProvider value={modalProps.size !== 'fullscreen'}>
                    <modalProps.component {...props} isModal />
                  </IsInModalProvider>
                )}
              </Stack.Screen>
            ),
          )}
        </Stack.Navigator>
        {Platform.OS !== 'ios' && <Modals />}
      </NavigationContainer>
    </>
  );
};
